/**
 * Application constants.
 */

// Where REST calls go. Same-origin in dev (Vite proxy) and on Vercel
// (where vercel.json rewrites /api/* through to the backend). Override
// with VITE_API_BASE_URL=https://api.example.com if you want to bypass
// the proxy and hit the backend directly.
export const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') || '/api';

// Where the WebSocket connects. Vercel `rewrites` only tunnel HTTP, not
// WebSocket upgrades, so on Vercel we MUST point this directly at the
// backend (e.g. wss://api.gammavision.app/ws). In dev it falls back
// to the same-origin URL so Vite's WS proxy handles it.
export const WS_BASE_URL = (() => {
  const fromEnv = (import.meta.env.VITE_WS_BASE_URL as string | undefined)?.replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  if (typeof window === 'undefined') return '/ws';
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}/ws`;
})();

export const DEFAULT_TICKER = 'SPY';

// Standard always-on index pills for the Options page. These render
// immediately and unconditionally, independent of the /api/tickers fetch,
// so the index row is "standard and always shown" even before the network
// resolves or if it fails. The backend pre-warms exactly this set; the
// equities group is merged on top of this list once /api/tickers returns.
export const STANDARD_INDICES = ['SPY', 'SPX', 'QQQ', 'NDX', 'IWM'] as const;

// Node indicators — same shape for KING + COUNTER_KING; the row CSS
// colors them differently (amber vs red) so the eye reads them as a
// sign-paired set without ambiguity.
export const NODE_INDICATORS: Record<string, string> = {
  king: '★',
  counter_king: '★',
  gatekeeper: '◆',
  hedge: '●',
  regular: '',
};

// Refresh intervals
export const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];

// After this many WebSocket reconnect failures, swap to polling fallback.
export const WS_POLL_FALLBACK_AFTER = 3;
export const POLL_INTERVAL_MS = 30_000;

// Authed nav links shown when the user has signed in.
export const NAV_LINKS = [
  { to: '/app/dashboard',   label: 'Dashboard',        end: false },
  { to: '/app/index',       label: 'GammaVision Index', end: false },
  { to: '/app/risk',        label: 'Risk Monitor',     end: false },
  { to: '/app/simulations', label: 'Simulations',      end: false },
  { to: '/app/scanner',     label: 'Index Analogues',  end: false },
  { to: '/app/options',     label: 'Options Heatmaps', end: false },
  { to: '/app/settings',    label: 'Settings',         end: false },
] as const;

// Routes that do NOT require auth. Consumed by AuthGate + any route guard
// that needs to decide whether to redirect unauthenticated visitors.
export const PUBLIC_ROUTES = ['/', '/login', '/signup', '/pricing', '/faq', '/terms', '/privacy'] as const;

// Storage keys — single source of truth. All localStorage reads/writes
// must use these constants so key drift is caught by TypeScript at
// compile time rather than silently creating orphan entries.
export const STORAGE_KEYS = {
  creds:              'gv2_creds',
  theme:              'gv2.theme',
  fontset:            'gv2.fontset',
  density:            'gv2.density',
  motion:             'gv2.motion',
  expLimit:           'gv2.expirationLimit',
  // Previously inline strings in their respective hooks/components:
  strikeWindow:       'gv2.strikeWindow',
  magnetMode:         'gv2.magnetMode',
  starnodesCollapsed: 'gv2.starnodes.collapsed',
  // Universe-expansion: tickers the user has discovered via search
  // (MRU) and explicitly starred. Lists live in useRecentTickers.ts.
  recentTickers:      'gv2.recentTickers',
  favoriteTickers:    'gv2.favoriteTickers',
  // Compare mode (Pro): the 2-3 tickers being compared side-by-side.
  compareTickers:     'gv2.compareTickers',
  // Day 3 launch-plan work: JWT issued by /api/auth/login + signup,
  // attached to REST as `Authorization: Bearer` and to WS as the
  // gv2-jwt subprotocol prefix. Legacy `creds` key (HTTP Basic) is
  // honoured as fallback during the transition.
  jwt:                'gv2.jwt',
} as const;
